import { useState } from 'react';
import { Loader2, MicVocal } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useAuthStore } from '@/lib/auth/store';
import { useCreateTtsPlan } from '../api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function parseNumber(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

export function AddTtsPlanSheet({ open, onOpenChange }: Props) {
  const userId = useAuthStore((s) => s.userId);
  const create = useCreateTtsPlan();

  const [planName, setPlanName] = useState('');
  const [perCharacter, setPerCharacter] = useState('');
  const [perVariable, setPerVariable] = useState('');

  const reset = () => {
    setPlanName('');
    setPerCharacter('');
    setPerVariable('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!planName.trim()) return toast.error('Give the plan a name.');
    if (!perCharacter.trim())
      return toast.error('Enter a per-character price.');
    if (!perVariable.trim())
      return toast.error('Enter a per-variable price.');

    create.mutate(
      {
        userId: Number(userId),
        planName: planName.trim(),
        perCharacter: parseNumber(perCharacter),
        perVariable: parseNumber(perVariable),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'TTS plan created');
          reset();
          onOpenChange(false);
        },
        onError: () => toast.error('Could not create the TTS plan.'),
      },
    );
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MicVocal className="h-4 w-4" />
            New TTS plan
          </SheetTitle>
          <SheetDescription>
            Define what users pay per synthesized character and per variable
            slot when this plan is assigned.
          </SheetDescription>
        </SheetHeader>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="plan-name">
              Plan name
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="plan-name"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g. tts_basic"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="per-char">
              Per character
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="per-char"
              inputMode="decimal"
              value={perCharacter}
              onChange={(e) =>
                setPerCharacter(e.target.value.replace(/[^\d.]/g, ''))
              }
              placeholder="e.g. 0.05"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="per-var">
              Per variable
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="per-var"
              inputMode="decimal"
              value={perVariable}
              onChange={(e) =>
                setPerVariable(e.target.value.replace(/[^\d.]/g, ''))
              }
              placeholder="e.g. 0.10"
            />
          </div>
          <SheetFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Create plan
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
